import React from 'react';
import { useForm } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { FormTitle } from 'src/financial/financial-data/financial-item/forms/common/FormTitle';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormContainer } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import { useJointOwnerDefault } from 'src/financial/financial-data/financial-item/forms/common/useJointOwnerDefault';
import {
  OWNER,
  resolveOwnersPayload,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import { useFlags } from 'src/feature-flags/FlagsHook';

type Props = {
  product: string;
  title: string;
  children?: React.ReactNode;
  defaultJointOwner?: boolean;
} & ProductFormComponentProps;

export function GenericFinancialItemFormWrapper(props: Props) {
  const { children, product, title, defaultJointOwner = false } = props;
  const { flagEnabledFinancialItemOwners } = useFlags();
  const ownersEnabled = flagEnabledFinancialItemOwners && defaultJointOwner;
  const { isCreateMode, uiData, initialValues, onDeleteConfirm, feedInfo, onConnectAsset } =
    getFinancialFormSetupValues(
      product,
      props,
      ownersEnabled ? [{ product: OWNER, field: 'owners' }] : [],
    );

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
  });

  useJointOwnerDefault(form, product, isCreateMode && defaultJointOwner);

  const handleSave = async (values: FormValuesType) => {
    const ownersPayload = resolveOwnersPayload(values, ownersEnabled, form);
    if (ownersPayload === null) return;

    await props.onSave(
      { ...values[product], ...ownersPayload },
      uiData.ui.entitiesMetadata[product],
    );
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete={'off'}>
        <FinancialFormContainer
          title={<FormTitle title={title} product={product} />}
          product={product}
          feedInfo={feedInfo}
          onConnect={onConnectAsset}
        >
          {children}
        </FinancialFormContainer>
      </form>
    </DynamicUiProviderWrapper>
  );
}
